import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import styles from './RoutePage.module.css';
import API_BASE_URL from '../config/api';
import { getTagStyle } from '../utils/tagUtils';
import { useUser } from '../context/UserContext'; 
import RoutePageSkeleton from '../components/RoutePageSkeleton';
import NotePopup from '../components/NotePopup';
import EditRoutePopup from '../components/EditRoutePopup';
import LoginPopup from '../components/LoginPopup';
import TranslateToggle from '../components/TranslateToggle';

export default function RoutePage() {
  const { routeId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentUser } = useUser();
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isNotePopupOpen, setIsNotePopupOpen] = useState(false);
  const [isLoginPopupOpen, setIsLoginPopupOpen] = useState(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [allMountains, setAllMountains] = useState([]);

  const handleAddNote = async ({ category, content, featureDuration }) => {
    if (!currentUser || !currentUser.token) {
        setIsLoginPopupOpen(true);
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/routes/${routeId}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentUser.token}`
        },
        body: JSON.stringify({ category, content, featureDuration })
      });

      const data = await res.json();
      if (data.success) {
        // Refresh route
        const fresh = await fetch(`${API_BASE_URL}/api/routes/${routeId}`);
        const freshJson = await fresh.json();
        if (freshJson.success) setRoute(freshJson.data);
      } else {
        alert(data.message || t('mountainPage.errors.addNote', 'Failed to add note'));
      }
    } catch (error) {
      console.error('Error adding note:', error);
      alert(t('mountainPage.errors.addNoteNetwork', 'Network error adding note'));
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!currentUser || !currentUser.token) {
        setIsLoginPopupOpen(true);
      return;
    }

    if (!window.confirm(t('mountainPage.prompts.deleteNote', 'Delete this note?'))) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/routes/${routeId}/notes/${encodeURIComponent(noteId)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${currentUser.token}`
        }
      });
      const data = await res.json();
      if (data.success) {
         setRoute(prev => {
             if (!prev) return prev;
             return {
                 ...prev,
                 tempNotes: (prev.tempNotes || []).filter(n => n.id !== noteId && n.createdAt !== noteId)
             };
         });
         // Also refresh to be sure
         const fresh = await fetch(`${API_BASE_URL}/api/routes/${routeId}`);
         const freshJson = await fresh.json();
         if (freshJson.success) setRoute(freshJson.data);
      } else {
        alert(data.message || t('mountainPage.alerts.generic', 'An error occurred'));
      }
    } catch (e) {
      console.error('Error deleting note:', e);
      alert(t('mountainPage.alerts.generic', 'An error occurred'));
    }
  };

  const handleEditClick = async () => {
       if (allMountains.length === 0) {
            try {
             const res = await fetch(`${API_BASE_URL}/api/mountains`);
             const data = await res.json();
             if (data.success) {
                 setAllMountains(data.data);
             }
           } catch(e) { console.error(e); }
       }
       setIsEditPopupOpen(true);
  };

  const handleEditSave = async (updatedData) => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/routes/${routeId}`, {
        method: 'PUT',
        headers: {
           'Content-Type': 'application/json',
           'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatedData)
      });
      
      if (!res.ok) throw new Error('Failed to update route');
      
      const data = await res.json();
      if (data.success) {
        setRoute(data.data); 
        setIsEditPopupOpen(false);
      } else {
        alert(data.message || 'Failed to update');
      }
    } catch (err) {
      console.error(err);
      alert('Error updating route');
    }
  };

  const handleDeleteRoute = async () => {
    if (!window.confirm(t('common.confirmDelete', 'Are you sure you want to delete this route?'))) return;
    if (!currentUser) return;

    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/routes/${routeId}`, {
        method: 'DELETE',
        headers: {
           'Authorization': `Bearer ${token}`
        }
      });
      
      if (!res.ok) throw new Error('Failed to delete route');
      
      navigate('/routes'); 
    } catch (err) {
      console.error(err);
      alert('Error deleting route');
    }
  };

  useEffect(() => {
    const fetchRoute = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/routes/${routeId}`);
        const data = await res.json();
        if (data.success) {
          setRoute(data.data);
        } else {
          setError(data.message || 'Failed to load route');
        }
      } catch (err) {
        console.error(err);
        setError('Network error');
      } finally {
        setLoading(false);
      }
    };

    if (routeId) fetchRoute();
  }, [routeId]);

  if (loading) return <RoutePageSkeleton />;
  if (error) return <div style={{textAlign: 'center', padding: '2rem', color: 'red'}}>{error}</div>;
  if (!route) return null;

  return (
    <div className={styles.container}>
      <Helmet>
        <title>{`${route.name}${route.mountains && route.mountains.length > 0 ? ' - ' + route.mountains.map(m => m.name).join(', ') : ''} | PoF`}</title>
      </Helmet>

      <button onClick={() => navigate(-1)} className={styles.backButton}>
        &larr; {t('action.back', { defaultValue: 'Back' })}
      </button>

      <div className={styles.header}>
        <h1 className={styles.title}>{route.name}</h1>
        <TranslateToggle text={`${route.name}\n\n${route.description || ''}`} />
      </div>

      <div className={styles.statsGrid}>
         <div className={styles.statCard}>
            <span className={styles.statLabel}>{t('common.distance', 'Distance')}</span>
            <span className={styles.statValue}>{route.kms ? `${route.kms} km` : '-'}</span>
         </div>
         <div className={styles.statCard}>
            <span className={styles.statLabel}>{t('common.elevationGain', 'Elevation Gain')}</span>
            <span className={styles.statValue}>{route.elevationGain ? `${route.elevationGain} m` : '-'}</span>
         </div>
         <div className={styles.statCard}>
            <span className={styles.statLabel}>{t('mountainPage.routes.difficulty')}</span>
            <span className={styles.statValue}>{route.difficulty || '-'}</span>
         </div>
         <div className={styles.statCard}>
            <span className={styles.statLabel}>{t('mountainPage.routes.type')}</span>
            <span className={styles.statValue}>
                {route.routeType === 'multi-day-huts'
                  ? t('mountainPage.routes.types.multiDayHuts')
                  : t('mountainPage.routes.types.oneDay')}
            </span>
         </div>
         {/* Mountains Card */}
         <div className={styles.statCard} style={{ gridColumn: 'span 2' }}>
            <span className={styles.statLabel}>{t('common.mountains', 'Mountains')}</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                {route.mountains && route.mountains.length > 0 ? route.mountains.map(m => (
                    <Link key={m.id} to={`/mountains/${m.id}`} className={styles.tag}>
                        {m.name}
                    </Link>
                )) : '-'}
            </div>
         </div>
         {/* Activities Card */}
         <div className={styles.statCard} style={{ gridColumn: 'span 2' }}>
            <span className={styles.statLabel}>{t('mountainPage.routes.activities', 'Activities')}</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                {route.tags && route.tags.length > 0 ? route.tags.map(tag => (
                   <span key={tag} className={styles.tag}>
                      {t(`mountainPage.routes.tags.${tag}`, { defaultValue: tag })}
                   </span>
                )) : '-'}
            </div>
         </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Route</h3>
        <div className={styles.timelineContainer}>
          {/* Start Point */}
           <div className={styles.timelineItem}>
              <div 
                 className={styles.timelineDot} 
                 style={{ backgroundColor: route.startColor || 'var(--color3)' }} 
               />
               <span className={styles.timelineText} style={{ fontWeight: 'bold' }}>{route.startPoint}</span>
           </div>

           {/* Waypoints */}
           {route.waypoints && route.waypoints.map((wp, idx) => {
              const name = typeof wp === 'object' ? wp.name : wp;
              const color = typeof wp === 'object' ? wp.color : null;
              return (
                 <div key={idx} className={styles.timelineItem}>
                    <div 
                       className={styles.timelineDot} 
                       style={{ backgroundColor: color || 'var(--color3)' }} 
                     />
                     <span className={styles.timelineText}>{name}</span>
                 </div>
              );
           })}

           {/* End Point (only if exists) */}
           {route.endPoint && (
              <div className={styles.timelineItem}>
                 <div 
                    className={styles.timelineDot}
                    style={{ backgroundColor: 'var(--color3)' }}
                  />
                  <span className={styles.timelineText} style={{ fontWeight: 'bold' }}>{route.endPoint}</span>
              </div>
           )}
        </div>
      </div>

      {route.description && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('mountainPage.routes.description')}</h3>
          <p className={styles.descriptionText}>{route.description}</p>
        </div>
      )}

      {/* Tags section removed */}

      <div className={styles.notesSection}>
          <h3>{t('mountainPage.notes.title', 'Notes')}</h3>
          <button
            onClick={() => setIsNotePopupOpen(prev => !prev)}
            className={styles.notesBtn}
          >
            {isNotePopupOpen ? t('mountainPage.notes.toggleCancel', 'Cancel') : t('mountainPage.notes.toggleAdd', 'Add Note')}
          </button>
          
          {/* Permanent Note */}
          {route.notes && <p>{route.notes}</p>}

          {/* Temporary notes */}
          {Array.isArray(route.tempNotes) && route.tempNotes.length > 0 && (
            <div className={styles.tempNotesList}>
              {route.tempNotes
                .filter(n => {
                  if (!n) return false;
                  if (!n.expiresAt) return true;
                  return Date.now() <= new Date(n.expiresAt).getTime();
                })
                .slice()
                .reverse()
                .map(note => (
                  <div key={note.id || `${note.createdAt}` } className={styles.noteItem}>
                    <div className={styles.noteHeader}>
                      <strong>{note.author || t('mountainPage.notes.anonymous', 'Anonymous')}</strong>
                      <span className={styles.noteMeta}>{note.category || 'Note'}</span>
                      <span className={styles.noteTime}>{new Date(note.createdAt).toLocaleString()}</span>
                      {(currentUser && (currentUser.admin || currentUser.editor || currentUser.username === note.author)) && (
                        <button
                          className={styles.noteDeleteBtn}
                          onClick={() => handleDeleteNote(note.id)}
                          title={t('mountainPage.notes.delete', 'Delete')}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <div className={styles.noteContent}>{note.content}</div>
                    {note.expiresAt && (
                      <div className={styles.noteExpires}>{t('mountainPage.notes.expires', { date: new Date(note.expiresAt).toLocaleString() })}</div>
                    )}
                  </div>
                ))}
            </div>
          )}

          {isEditPopupOpen && (
        <EditRoutePopup
          route={route}
          mountains={allMountains}
          onClose={() => setIsEditPopupOpen(false)}
          onSave={handleEditSave}
        />
      )}

      {isNotePopupOpen && (
            <NotePopup
              targetId={routeId}
              targetName="Route"
              itemName={route?.name}
              onClose={() => setIsNotePopupOpen(false)}
              onSubmit={handleAddNote}
              allowPermanent={true}
            />
          )}
      </div>

      {isLoginPopupOpen && <LoginPopup onClose={() => setIsLoginPopupOpen(false)} />}
    </div>
  );
}
